import React from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import { useTestDataContext } from 'contexts/TestDataContext';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import {
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE
} from 'routes/RouteList';
import useTabIndex from 'hooks/utils/useTabIndex';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import TabLink from 'components/tabs/TabLink';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import Badge from 'components/badge/Badge';
import PersonSessionReportTab from 'containers/pages/persons/PersonSessionReportTab';
import PersonSessionDataTab from 'containers/pages/persons/PersonSessionDataTab';
import { useSessionContext } from 'contexts/SessionContext';

const PersonSessionTabs = () => {
  const { entityId, sessionId, groupId, personId, testDataId, programId } =
    useParams();
  const { session } = useSessionContext();
  const { hasResults } = useTestDataContext();
  const ability = useAbility(AbilityContext);
  const tabRoutes = [
    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA
  ];
  if (ability.can('update', 'TestData') && session.isTestSetV2) {
    tabRoutes.push([
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
      ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA_EDIT
    ]);
  }

  const [tabIndex, setTabIndex] = useTabIndex(tabRoutes);
  const isAdviceView = useRouteMatch(
    ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE
  );

  return (
    <Tabs
      selectedIndex={tabIndex}
      onSelect={index => setTabIndex(index)}
      fullWidth={isAdviceView}
    >
      <TabList>
        {ability.can('read', 'TestData') && (
          <TabLink
            key="session.sporter.report"
            to={
              testDataId &&
              generatePath(
                ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_REPORT_TESTDATA,
                {
                  entityId,
                  groupId,
                  sessionId,
                  personId,
                  testDataId
                }
              )
            }
            disabled={!hasResults()}
          >
            <FormattedMessage {...messages.tabSessionSporterReport} />
          </TabLink>
        )}
        {ability.can('update', 'TestData') && session.isTestSetV2 && (
          <TabLink
            key="session.sporter.data"
            to={
              testDataId
                ? generatePath(
                    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS_TESTDATA,
                    {
                      entityId,
                      groupId,
                      sessionId,
                      personId,
                      testDataId
                    }
                  )
                : generatePath(
                    ROUTE_PERSONS_ENTITY_PERSON_SESSIONS_SESSION_TESTS,
                    {
                      entityId,
                      groupId,
                      sessionId,
                      personId
                    }
                  )
            }
          >
            <FormattedMessage {...messages.tabSessionSporterTests} />
          </TabLink>
        )}

        {isAdviceView && (
          <TabLink
            key="session.sporter.advice"
            to={generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON_ADVICE, {
              entityId,
              sessionsTab: 'sessions',
              sessionId,
              personId,
              programId
            })}
          >
            <Badge>
              <FormattedMessage {...messages.badgeSessionSporterAdvice} />
            </Badge>
            <FormattedMessage {...messages.tabSessionSporterAdvice} />
          </TabLink>
        )}
      </TabList>

      {/* Tab > REPORT */}
      {ability.can('read', 'TestData') && (
        <TabPanel key="session.sporter.report">
          <TabPanelBody withScroller>
            <PersonSessionReportTab />
          </TabPanelBody>
        </TabPanel>
      )}
      {/* Tab > DATA */}
      {ability.can('update', 'TestData') && session.isTestSetV2 && (
        <TabPanel key="session.sporter.data">
          <TabPanelBody withScroller>
            <PersonSessionDataTab />
          </TabPanelBody>
        </TabPanel>
      )}
    </Tabs>
  );
};

export default PersonSessionTabs;
